import React from 'react';
import { Copy, RefreshCw, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

/**
 * Modal para clonar muestras con preview de números GOYA
 * Soporta dos variantes: normal y afterCreate (después de crear una muestra)
 */
const CloneModal = ({
  open,
  onOpenChange,
  quantity,
  onQuantityChange,
  onConfirm,
  onSkip, // Solo para variante afterCreate
  isLoading,
  currentNumGoya,
  previewNumbers = [],
  variant = 'normal', // 'normal' | 'afterCreate'
}) => {
  const isAfterCreate = variant === 'afterCreate';
  
  // Colores según variante
  const accentColor = isAfterCreate ? 'green' : 'blue';
  const bgColorClass = isAfterCreate ? 'bg-green-50' : 'bg-blue-50';
  const borderColorClass = isAfterCreate ? 'border-green-200' : 'border-blue-200';
  const textColorClass = isAfterCreate ? 'text-green-700' : 'text-blue-700';
  const badgeColorClass = isAfterCreate ? 'bg-green-500' : 'bg-blue-500';
  const buttonGradient = isAfterCreate 
    ? 'bg-gradient-to-r from-green-600 to-blue-600' 
    : 'bg-gradient-to-r from-blue-600 to-purple-600';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="clone-modal">
        <DialogHeader>
          <DialogTitle className={isAfterCreate ? 'flex items-center gap-2 text-green-600' : ''}>
            {isAfterCreate && <Check className="h-6 w-6" />}
            {isAfterCreate ? 'Muestra Creada Correctamente' : 'Clonar Estudio'}
          </DialogTitle>
          <DialogDescription>
            {isAfterCreate 
              ? '¿Deseas crear copias (clones) de esta muestra?'
              : 'Crea copias automáticas de este estudio con Nº GOYA incrementado'
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{isAfterCreate ? 'Nº GOYA de la muestra creada' : 'Nº GOYA actual'}</Label>
            <div className={`p-3 ${bgColorClass} rounded-lg border ${borderColorClass}`}>
              <span className={`font-mono font-bold ${textColorClass}`}>
                {currentNumGoya || 'N/A'}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clone-quantity">¿Cuántas copias crear?</Label>
            <Input
              id="clone-quantity"
              type="number"
              min="1"
              max="10"
              value={quantity}
              onChange={(e) => onQuantityChange(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
              className="text-center text-lg font-semibold"
              data-testid="clone-quantity-input"
            />
            <p className="text-xs text-gray-500">Máximo: 10 copias</p>
          </div>

          <div className="space-y-2">
            <Label>Se crearán los siguientes Nº GOYA:</Label>
            <div className="max-h-40 overflow-y-auto p-3 bg-gray-50 rounded-lg border">
              {previewNumbers.length > 0 ? (
                <ul className="space-y-1">
                  {previewNumbers.map((numGoya, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <span className={`w-6 h-6 flex items-center justify-center ${badgeColorClass} text-white rounded-full text-xs font-bold`}>
                        {index + 1}
                      </span>
                      <span className="font-mono font-semibold text-gray-700">
                        Nº GOYA: {numGoya}
                      </span>
                      {!isAfterCreate && (
                        <span className="text-xs text-gray-500 ml-auto">
                          (Producto: vacío)
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 text-center">
                  {isAfterCreate 
                    ? 'El Nº GOYA debe ser numérico para crear clones'
                    : 'El Nº GOYA debe ser numérico'
                  }
                </p>
              )}
            </div>
          </div>

          <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-xs text-yellow-800">
              <strong>Nota:</strong> Los clones tendrán todos los campos igual al original,
              excepto el campo <strong>Producto</strong> que quedará vacío para que lo edites
              {isAfterCreate ? '.' : ' después.'}
            </p>
          </div>
        </div>

        <DialogFooter className={isAfterCreate ? 'flex gap-2' : ''}>
          <Button
            type="button"
            variant="outline"
            onClick={isAfterCreate ? onSkip : () => onOpenChange(false)}
            disabled={isLoading}
            data-testid="clone-cancel-btn"
          >
            {isAfterCreate ? 'No, solo crear esta muestra' : 'Cancelar'}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isLoading || previewNumbers.length === 0}
            className={buttonGradient}
            data-testid="clone-confirm-btn"
          >
            {isLoading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Clonando...
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                {isAfterCreate 
                  ? `Sí, crear ${quantity} ${quantity === 1 ? 'clon' : 'clones'}`
                  : `Crear ${quantity} ${quantity === 1 ? 'Clon' : 'Clones'}`
                }
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CloneModal;
